/// The CADET diagram — explains how compass, magnetic and true relate
/// and which way the sign rule runs. A vertical ladder (True at the
/// top, Compass at the bottom) with the two corrections between the
/// rungs, and a rail spelling out the CADET mnemonic and its reverse.
///
/// Pure presentational component; the wording mirrors
/// `@nav-games/compass`'s module docs so the maths and the teaching
/// stay in step.

function Rung({
  title,
  subtitle,
  tone,
}: {
  title: string;
  subtitle: string;
  tone: "true" | "magnetic" | "compass";
}) {
  const border =
    tone === "true"
      ? "var(--accent)"
      : tone === "compass"
        ? "var(--accent-hi)"
        : "var(--border-strong)";
  return (
    <div
      className="card"
      style={{
        margin: 0,
        padding: "10px 14px",
        borderLeft: `4px solid ${border}`,
        textAlign: "center",
      }}
    >
      <div style={{ fontWeight: 700, letterSpacing: "0.04em" }}>{title}</div>
      <div className="small muted">{subtitle}</div>
    </div>
  );
}

function Correction({ label }: { label: string }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        padding: "2px 0",
        color: "var(--text-muted)",
      }}
    >
      <span aria-hidden style={{ fontSize: 16, lineHeight: 1 }}>
        ↕
      </span>
      <span className="small">
        apply <strong>{label}</strong>
      </span>
    </div>
  );
}

export function CompassCadetDiagram() {
  return (
    <div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) auto",
          gap: 16,
          alignItems: "stretch",
        }}
      >
        {/* The ladder of norths. */}
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <Rung title="TRUE" subtitle="chart north" tone="true" />
          <Correction label="VARIATION — from the chart's compass rose" />
          <Rung title="MAGNETIC" subtitle="magnetic north" tone="magnetic" />
          <Correction label="DEVIATION — from the deviation card (varies with heading)" />
          <Rung
            title="COMPASS"
            subtitle="what the steering compass reads"
            tone="compass"
          />
        </div>

        {/* Direction rail — which way the sign rule runs. */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "center",
            minWidth: 96,
            textAlign: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <span aria-hidden style={{ fontSize: 22, color: "var(--accent-hi)" }}>
              ▲
            </span>
            <span className="small" style={{ fontWeight: 600, color: "var(--accent-hi)" }}>
              ADD East
            </span>
            <span className="small muted">subtract West</span>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <span className="small muted">add West</span>
            <span className="small" style={{ fontWeight: 600 }}>
              subtract East
            </span>
            <span aria-hidden style={{ fontSize: 22, color: "var(--text-muted)" }}>
              ▼
            </span>
          </div>
        </div>
      </div>

      {/* The mnemonic spelled out, plus the companion check. */}
      <div
        className="card"
        style={{ margin: "12px 0 0", padding: "10px 14px", background: "var(--accent-soft)" }}
      >
        <div style={{ fontSize: 16 }}>
          <strong style={{ color: "var(--accent)" }}>C</strong>ompass{" "}
          <strong style={{ color: "var(--accent)" }}>AD</strong>d{" "}
          <strong style={{ color: "var(--accent)" }}>E</strong>ast →{" "}
          <strong style={{ color: "var(--accent)" }}>T</strong>rue{" "}
          <span className="muted">— CADET</span>
        </div>
        <p className="small muted" style={{ margin: "6px 0 0" }}>
          Going up to True, <strong>add</strong> easterly errors and{" "}
          <strong>subtract</strong> westerly ones; reverse it going back down
          to Compass. Check yourself with{" "}
          <em>“Error East, Compass Least; Error West, Compass Best.”</em>
        </p>
      </div>
    </div>
  );
}
